import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiError, readApiErrorResponse } from "@/lib/api-error";
import { apiUrl } from "@/services/http/api-url";
import { platformFetch } from "@/services/http/platform-fetch";

type Provider = "razorpay" | "stripe";

/** `GET …/payment-config` (Plan 2 S12): never a secret, only the hint / fingerprint. */
export type PaymentConfigView = {
  provider: Provider | null;
  currency: "INR" | "USD" | null;
  configured: boolean;
  keyFingerprint: string | null;
  keyHint: string | null;
  webhookPath: string | null;
};

const FIELDS: Record<Provider, Array<"keyId" | "keySecret" | "webhookSecret" | "secretKey">> = {
  razorpay: ["keyId", "keySecret", "webhookSecret"],
  stripe: ["secretKey", "webhookSecret"],
};
const CURRENCY: Record<Provider, "INR" | "USD"> = { razorpay: "INR", stripe: "USD" };
const INPUT =
  "w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-[var(--text-primary)]";

/**
 * F2: the gateway posts to the API, which in production is on another origin
 * (`VITE_API_BASE_URL`, split mode). Only a relative base (dev, same origin) gets the page origin.
 */
export function absoluteWebhookUrl(webhookPath: string): string {
  const url = apiUrl(webhookPath);
  return /^https?:\/\//i.test(url) ? url : `${window.location.origin}${url}`;
}

/**
 * MT-1 Plan 2 S16 (FR-9 UI): the platform admin sets a school's gateway. Secrets are write-only:
 * the inputs are empty on load, a blank one keeps the saved value (only typed fields are sent),
 * and the page shows the saved key's hint (or fingerprint) and the absolute webhook URL.
 */
export function SchoolPaymentConfig({ tenantId }: { tenantId: string }) {
  const t = useTranslations("platform.paymentConfig");
  const tRoot = useTranslations();
  const queryClient = useQueryClient();
  const queryKey = ["platform-payment-config", tenantId];
  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<PaymentConfigView> => {
      const res = await platformFetch(`/platform/admin/tenants/${tenantId}/payment-config`);
      if (!res.ok) throw await readApiErrorResponse(res);
      return ((await res.json()) as { data: PaymentConfigView }).data;
    },
  });
  const [chosen, setChosen] = useState<Provider | "">("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"copied" | "failed" | null>(null);

  const provider: Provider | "" = chosen || query.data?.provider || "";
  const sameProvider = Boolean(query.data?.configured && query.data.provider === provider);

  function savedNote(): string {
    if (!sameProvider || !query.data) return "";
    if (query.data.keyHint) return t("savedHint", { hint: query.data.keyHint });
    if (query.data.keyFingerprint) {
      return t("savedFingerprint", { fingerprint: query.data.keyFingerprint });
    }
    return t("keepEmpty");
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!provider) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    const credentials = Object.fromEntries(
      FIELDS[provider].filter((f) => (values[f] ?? "") !== "").map((f) => [f, values[f]!]),
    );
    try {
      const res = await platformFetch(`/platform/admin/tenants/${tenantId}/payment-config`, {
        method: "PUT",
        body: JSON.stringify({ provider, currency: CURRENCY[provider], credentials }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      setValues({});
      setMessage(t("saved"));
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ["platform-school", tenantId] });
    } catch (err) {
      const parsed = readApiError(err);
      const field = parsed?.issues?.[0]?.path.replace(/^credentials\./, "");
      if (
        parsed?.code === "VALIDATION_ERROR" &&
        field &&
        field in { keyId: 1, keySecret: 1, webhookSecret: 1, secretKey: 1 }
      ) {
        setError(t("enterAgain", { field: t(`fields.${field}` as "fields.keyId") }));
      } else {
        setError(
          apiErrorText(err, tRoot, "platform.paymentConfig.saveFailed", {
            namespace: "platform.errors",
          }),
        );
      }
    } finally {
      setSaving(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  const webhookPath = provider ? `/api/v1/webhooks/${provider}/${tenantId}` : null;
  const webhookUrl = webhookPath ? absoluteWebhookUrl(webhookPath) : null;

  return (
    <section
      className="mt-6 rounded-lg border border-[var(--border-strong)] p-4"
      aria-labelledby="school-payment-title"
      data-testid="school-payment-config"
    >
      <h2 id="school-payment-title" className="text-lg font-semibold">
        {t("title")}
      </h2>
      {query.isError && (
        <p role="alert" className="mt-2 text-sm text-[var(--danger)]">
          {apiErrorText(query.error, tRoot, "platform.paymentConfig.loadFailed", {
            namespace: "platform.errors",
          })}
        </p>
      )}
      {query.data && (
        <form onSubmit={save} className="mt-3 space-y-3">
          <div>
            <label htmlFor="payment-provider" className="mb-1 block text-sm">
              {t("provider")}
            </label>
            <select
              id="payment-provider"
              value={provider}
              onChange={(e) => {
                setChosen(e.target.value as Provider);
                setValues({});
              }}
              className={INPUT}
            >
              <option value="" disabled>
                {t("providerNone")}
              </option>
              <option value="razorpay">{t("razorpay")}</option>
              <option value="stripe">{t("stripe")}</option>
            </select>
            {provider && (
              <p className="mt-1 text-xs text-[var(--text-muted)]">
                {t("currency", { currency: CURRENCY[provider] })}
              </p>
            )}
          </div>
          {provider &&
            FIELDS[provider].map((field) => {
              const id = `payment-${field}`;
              const label = t(provider === "razorpay" ? "fieldLabel" : "stripeFieldLabel", {
                field: t(`fields.${field}`),
              });
              const note = savedNote();
              return (
                <div key={field}>
                  <label htmlFor={id} className="mb-1 block text-sm">
                    {label}
                  </label>
                  <input
                    id={id}
                    type={field === "keyId" ? "text" : "password"}
                    autoComplete="off"
                    spellCheck={false}
                    value={values[field] ?? ""}
                    aria-describedby={note ? `${id}-note` : undefined}
                    onChange={(e) => setValues((v) => ({ ...v, [field]: e.target.value }))}
                    className={INPUT}
                  />
                  {note && (
                    <p id={`${id}-note`} className="mt-1 text-xs text-[var(--text-muted)]">
                      {note}
                    </p>
                  )}
                </div>
              );
            })}
          <button
            type="submit"
            disabled={saving || !provider}
            className="rounded-md border border-[var(--border-strong)] px-3 py-2 text-sm"
          >
            {saving ? t("saving") : t("save")}
          </button>
          <div aria-live="polite">{message && <p className="text-sm">{message}</p>}</div>
          {error && (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {error}
            </p>
          )}
        </form>
      )}
      {webhookUrl && (
        <div className="mt-4 text-sm">
          <p>{t("webhookUrl")}</p>
          <code className="block break-all" data-testid="payment-webhook-url">
            {webhookUrl}
          </code>
          <button
            type="button"
            onClick={() => void copy(webhookUrl)}
            className="mt-2 rounded-md border border-[var(--border-strong)] px-3 py-1 text-sm"
          >
            {t("copy")}
          </button>
          <p aria-live="polite" className="mt-1 text-xs">
            {copyState === "copied" ? t("copied") : copyState === "failed" ? t("copyFailed") : ""}
          </p>
        </div>
      )}
    </section>
  );
}
